"use client";

import { useMemo } from "react";
import { findMissingCards, missingCardsSummary } from "@/lib/deck-gaps";
import { merchantLinksForCard } from "@/lib/merchant-links";
import type { InventoryEntry, SavedDeck } from "@/lib/local-store";
import type { CardRecord } from "@/lib/types";

type Props = {
  deck: SavedDeck;
  inventory: InventoryEntry[];
  cards: CardRecord[];
  compact?: boolean;
};

export default function MissingCardsPanel({ deck, inventory, cards, compact }: Props) {
  const missing = useMemo(
    () => findMissingCards(deck, inventory, cards),
    [deck, inventory, cards],
  );
  const summary = missingCardsSummary(missing);

  if (missing.length === 0) {
    return (
      <div
        className={`rounded-2xl border border-emerald-500/20 bg-emerald-500/5 ${
          compact ? "p-3" : "p-4"
        }`}
      >
        <p className="text-sm font-medium text-emerald-200">
          Inventaire OK pour « {deck.name} » — aucune carte manquante.
        </p>
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl border border-amber-500/25 bg-amber-500/5 ${compact ? "p-3" : "p-4"}`}
    >
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h3 className="text-sm font-bold uppercase tracking-wide text-amber-200/90">
            Cartes manquantes
          </h3>
          <p className="mt-1 text-sm text-gray-400">
            {summary.unique} carte{summary.unique > 1 ? "s" : ""} · {summary.copies} copie
            {summary.copies > 1 ? "s" : ""} à choper pour « {deck.name} »
          </p>
        </div>
      </div>

      <ul className={`space-y-2 ${compact ? "max-h-56 overflow-y-auto" : ""}`}>
        {missing.map((m) => {
          const links = merchantLinksForCard(m.card);
          return (
            <li
              key={m.cardId}
              className="flex flex-wrap items-center gap-2 rounded-xl border border-white/5 bg-black/25 px-3 py-2"
            >
              {m.card.imageUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={m.card.imageUrl}
                  alt=""
                  className="h-10 w-7 rounded object-cover"
                />
              ) : (
                <div className="flex h-10 w-7 items-center justify-center rounded bg-white/5 text-[10px] text-gray-600">
                  ?
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{m.card.name}</p>
                <p className="text-[11px] text-gray-500">
                  {[m.card.setId, m.card.collectorNumber].filter((v) => v != null && v !== "").join(" ")}
                  {" · "}
                  possédé {m.owned}/{m.needed}
                  {m.sources.includes("sideboard") ? " · SB" : ""}
                </p>
              </div>
              <span className="rounded-lg bg-amber-500/20 px-2 py-1 text-xs font-bold tabular-nums text-amber-100">
                −{m.missing}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {links.map((link) => (
                  <a
                    key={link.id}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-[11px] font-medium text-cyan-200 hover:bg-white/10"
                  >
                    {link.label} ↗
                  </a>
                ))}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
